import { create } from 'zustand';
import { API_URL, apiFetch, authHeaders, useAppStore } from '../../store';

const LIST_POLL_MS = 12000;
const CHAT_POLL_MS = 2500;
const attachmentCache = new Map();
const conversationAvatarCache = new Map();

const request = async (path, options = {}) => {
  const isForm = options.body instanceof FormData;
  const res = await apiFetch(path, {
    ...options,
    headers: { ...authHeaders(!isForm), ...(options.headers || {}) },
  });
  if (!res.ok) {
    let message = 'Не удалось выполнить запрос';
    try { message = (await res.json()).error || message; } catch (_) {}
    const error = new Error(message);
    error.status = res.status;
    throw error;
  }
  if (res.status === 204) return null;
  return res.json();
};

const notify = (message, type = 'error') => {
  try { useAppStore.getState().fire(message, type); } catch (_) {}
};

const uid = () => `web-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

const mergeMessages = (current = [], incoming = []) => {
  const byId = new Map(current.map((item) => [String(item.id), item]));
  incoming.forEach((item) => byId.set(String(item.id), { ...byId.get(String(item.id)), ...item }));
  return [...byId.values()].sort((a, b) => {
    const ai = Number(a.id), bi = Number(b.id);
    if (Number.isFinite(ai) && Number.isFinite(bi)) return ai - bi;
    return new Date(a.at) - new Date(b.at);
  });
};

const mergeConversation = (current, incoming) => ({
  ...(current || {}),
  ...incoming,
  messages: incoming.messages
    ? mergeMessages(current?.messages, incoming.messages)
    : (incoming.lastMessage
      ? mergeMessages(current?.messages, [incoming.lastMessage])
      : (current?.messages || [])),
});

const replaceConversation = (items, incoming) => {
  const found = items.some((item) => item.id === incoming.id);
  const next = found
    ? items.map((item) => item.id === incoming.id ? mergeConversation(item, incoming) : item)
    : [mergeConversation(null, incoming), ...items];
  return next.sort((a, b) => Number(b.pinned) - Number(a.pinned)
    || new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
};

export async function attachmentObjectUrl(attachment) {
  if (!attachment?.id) return '';
  if (attachmentCache.has(attachment.id)) return attachmentCache.get(attachment.id);
  const res = await apiFetch(`${API_URL}${attachment.url}`, { headers: authHeaders(false) });
  if (!res.ok) throw new Error('Не удалось загрузить фотографию');
  const url = URL.createObjectURL(await res.blob());
  attachmentCache.set(attachment.id, url);
  return url;
}

export async function conversationAvatarObjectUrl(chat) {
  if (!chat?.avatar?.url) return '';
  const key = `${chat.id}:${chat.avatar.version || 0}`;
  if (conversationAvatarCache.has(key)) return conversationAvatarCache.get(key);
  const res = await apiFetch(`${API_URL}${chat.avatar.url}`, { headers: authHeaders(false) });
  if (!res.ok) throw new Error('Не удалось загрузить аватар группы');
  const url = URL.createObjectURL(await res.blob());
  conversationAvatarCache.set(key, url);
  return url;
}

export const useMessenger = create((set, get) => ({
  conversations: [],
  contacts: [],
  blockedUsers: [],
  ready: false,
  loading: false,
  error: null,
  activeId: null,
  listTimer: null,
  chatTimer: null,
  syncAfter: {},

  hydrate: async ({ silent = false } = {}) => {
    try { localStorage.removeItem('sko_messenger_frontend_v1'); } catch (_) {}
    if (!localStorage.getItem('access_token')) {
      set({ conversations: [], contacts: [], ready: true, loading: false });
      return [];
    }
    if (!silent) set({ loading: true, error: null });
    try {
      const data = await request('/messenger/conversations', { method: 'GET' });
      set((state) => ({
        conversations: (data.conversations || []).reduce(
          (items, item) => replaceConversation(items, item), state.conversations,
        ).filter((item) => (data.conversations || []).some((fresh) => fresh.id === item.id)),
        ready: true,
        loading: false,
        error: null,
      }));
      return data.conversations || [];
    } catch (error) {
      set({ ready: true, loading: false, error: error.message });
      if (!silent) notify(error.message);
      return [];
    }
  },

  startListPolling: () => {
    if (get().listTimer) return;
    get().hydrate();
    const tick = () => {
      if (document.visibilityState === 'visible' && localStorage.getItem('access_token')) {
        get().hydrate({ silent: true });
      }
    };
    const timer = window.setInterval(tick, LIST_POLL_MS);
    window.addEventListener('focus', tick);
    set({ listTimer: { timer, tick } });
  },

  stopPolling: () => {
    const { listTimer, chatTimer } = get();
    if (listTimer) {
      window.clearInterval(listTimer.timer);
      window.removeEventListener('focus', listTimer.tick);
    }
    if (chatTimer) window.clearInterval(chatTimer);
    if (!localStorage.getItem('access_token')) {
      attachmentCache.forEach((url) => URL.revokeObjectURL(url));
      attachmentCache.clear();
      set({ conversations: [], contacts: [], ready: false, listTimer: null, chatTimer: null, activeId: null });
    } else {
      set({ listTimer: null, chatTimer: null, activeId: null });
    }
  },

  searchUsers: async (query = '', updateStore = true) => {
    try {
      const data = await request(`/messenger/users?q=${encodeURIComponent(query.trim())}`);
      if (updateStore) set({ contacts: data.users || [] });
      return data.users || [];
    } catch (error) {
      notify(error.message);
      return [];
    }
  },

  loadConversation: async (conversationId, { silent = false } = {}) => {
    if (!silent) set({ loading: true });
    try {
      const data = await request(`/messenger/conversations/${conversationId}`);
      set((state) => ({
        conversations: replaceConversation(state.conversations, data.conversation),
        loading: false,
        syncAfter: { ...state.syncAfter, [conversationId]: new Date(Date.now() - 5000).toISOString() },
      }));
      return data.conversation;
    } catch (error) {
      set({ loading: false, error: error.message });
      if (!silent) notify(error.message);
      return null;
    }
  },

  loadOlder: async (conversationId) => {
    const chat = get().conversations.find((item) => item.id === conversationId);
    const first = chat?.messages?.find((item) => Number.isFinite(Number(item.id)) && Number(item.id) > 0);
    if (!first) return false;
    try {
      const data = await request(`/messenger/conversations/${conversationId}/messages?before_id=${first.id}&limit=50`);
      set((state) => ({
        conversations: state.conversations.map((item) => item.id === conversationId
          ? { ...item, messages: mergeMessages(data.messages, item.messages), hasMore: data.hasMore }
          : item),
      }));
      return data.hasMore;
    } catch (error) {
      notify(error.message);
      return false;
    }
  },

  pollConversation: async (conversationId) => {
    const since = get().syncAfter[conversationId] || new Date(Date.now() - 5000).toISOString();
    try {
      const data = await request(`/messenger/conversations/${conversationId}/messages?changed_after=${encodeURIComponent(since)}&limit=100`);
      set((state) => ({
        conversations: state.conversations.map((item) => item.id === conversationId ? {
          ...item,
          messages: mergeMessages(item.messages, data.messages).map((message) => (
            message.own && Number(message.id) <= Number(data.readThrough)
              ? { ...message, read: true } : message
          )),
        } : item),
        syncAfter: { ...state.syncAfter, [conversationId]: data.serverTime },
      }));
    } catch (_) {}
  },

  startChatPolling: (conversationId) => {
    if (get().chatTimer) window.clearInterval(get().chatTimer);
    set({ activeId: conversationId });
    if (!conversationId) { set({ chatTimer: null }); return; }
    get().loadConversation(conversationId).then(() => get().markRead(conversationId));
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') get().pollConversation(conversationId);
    }, CHAT_POLL_MS);
    set({ chatTimer: timer });
  },

  createDirect: async (contact) => {
    try {
      const data = await request('/messenger/conversations', {
        method: 'POST', body: JSON.stringify({ type: 'direct', userId: contact.id }),
      });
      set((state) => ({ conversations: replaceConversation(state.conversations, data.conversation) }));
      return data.conversation.id;
    } catch (error) {
      notify(error.message);
      return null;
    }
  },

  createGroup: async (title, memberIds) => {
    try {
      const data = await request('/messenger/conversations', {
        method: 'POST', body: JSON.stringify({ type: 'group', title: title.trim(), memberIds }),
      });
      set((state) => ({ conversations: replaceConversation(state.conversations, data.conversation) }));
      return data.conversation.id;
    } catch (error) {
      notify(error.message);
      return null;
    }
  },

  sendMessage: async (conversationId, text, images = [], extra = {}) => {
    const clientId = uid();
    const user = useAppStore.getState().user;
    const optimistic = {
      id: `pending-${clientId}`, conversationId, authorId: user?.id,
      author: user?.nickname || user?.full_name || 'Вы', text: text.trim(),
      kind: images.length ? 'image' : 'text', at: new Date().toISOString(), own: true,
      read: false, pending: true, attachments: images.map((file, index) => ({
        id: `local-${index}`, name: file.name, localUrl: URL.createObjectURL(file),
      })), reactions: [], replyTo: extra.reply || null,
    };
    set((state) => ({ conversations: state.conversations.map((chat) => chat.id === conversationId
      ? { ...chat, messages: [...(chat.messages || []), optimistic], unread: 0 }
      : chat) }));
    try {
      const form = new FormData();
      if (text.trim()) form.append('text', text.trim());
      form.append('client_message_id', clientId);
      if (extra.reply_to_id) form.append('reply_to_id', extra.reply_to_id);
      if (extra.forward_message_id) form.append('forward_message_id', extra.forward_message_id);
      images.forEach((file) => form.append('images', file));
      const data = await request(`/messenger/conversations/${conversationId}/messages`, {
        method: 'POST', body: form,
      });
      set((state) => ({ conversations: state.conversations.map((chat) => chat.id === conversationId
        ? { ...chat, messages: mergeMessages(
          (chat.messages || []).filter((message) => message.id !== optimistic.id), [data.message]), unread: 0 }
        : chat) }));
      get().hydrate({ silent: true });
      return data.message;
    } catch (error) {
      set((state) => ({ conversations: state.conversations.map((chat) => chat.id === conversationId
        ? { ...chat, messages: (chat.messages || []).map((message) => message.id === optimistic.id
          ? { ...message, pending: false, failed: true } : message) }
        : chat) }));
      notify(error.message);
      return null;
    }
  },

  editMessage: async (messageId, text) => {
    try {
      const data = await request(`/messenger/messages/${messageId}`, {
        method: 'PATCH', body: JSON.stringify({ text }),
      });
      set((state) => ({ conversations: state.conversations.map((chat) => ({
        ...chat, messages: mergeMessages(chat.messages, [data.message]),
      })) }));
      return true;
    } catch (error) { notify(error.message); return false; }
  },

  deleteMessage: async (messageId) => {
    try {
      const data = await request(`/messenger/messages/${messageId}`, { method: 'DELETE' });
      set((state) => ({ conversations: state.conversations.map((chat) => ({
        ...chat, messages: mergeMessages(chat.messages, [data.message]),
      })) }));
      return true;
    } catch (error) { notify(error.message); return false; }
  },

  toggleReaction: async (messageId, emoji) => {
    try {
      const data = await request(`/messenger/messages/${messageId}/reactions`, {
        method: 'POST', body: JSON.stringify({ emoji }),
      });
      set((state) => ({ conversations: state.conversations.map((chat) => ({
        ...chat,
        messages: (chat.messages || []).map((message) => message.id === messageId
          ? { ...message, reactions: data.reactions } : message),
      })) }));
    } catch (error) { notify(error.message); }
  },

  markRead: async (conversationId) => {
    const chat = get().conversations.find((item) => item.id === conversationId);
    const last = [...(chat?.messages || [])].reverse().find((item) => Number(item.id) > 0);
    set((state) => ({ conversations: state.conversations.map((item) => item.id === conversationId
      ? { ...item, unread: 0 } : item) }));
    try {
      await request(`/messenger/conversations/${conversationId}/read`, {
        method: 'PUT', body: JSON.stringify({ messageId: last?.id || null }),
      });
    } catch (_) {}
  },

  updateConversation: async (conversationId, patch) => {
    const previous = get().conversations.find((item) => item.id === conversationId);
    set((state) => ({ conversations: state.conversations.map((item) => item.id === conversationId ? { ...item, ...patch } : item) }));
    try {
      const data = await request(`/messenger/conversations/${conversationId}`, {
        method: 'PATCH', body: JSON.stringify(patch),
      });
      set((state) => ({ conversations: replaceConversation(state.conversations, data.conversation) }));
      return data.conversation;
    } catch (error) {
      if (previous) set((state) => ({ conversations: state.conversations.map((item) => item.id === conversationId ? previous : item) }));
      notify(error.message); return null;
    }
  },

  updateConversationAvatar: async (conversationId, file = null) => {
    try {
      const options = file ? { method: 'PUT', body: new FormData() } : { method: 'DELETE' };
      if (file) options.body.append('avatar', file);
      const data = await request(`/messenger/conversations/${conversationId}/avatar`, options);
      set((state) => ({ conversations: replaceConversation(state.conversations, data.conversation) }));
      return data.conversation;
    } catch (error) { notify(error.message); return null; }
  },

  addMembers: async (conversationId, memberIds) => {
    try {
      const data = await request(`/messenger/conversations/${conversationId}/members`, {
        method: 'POST', body: JSON.stringify({ memberIds }),
      });
      set((state) => ({ conversations: replaceConversation(state.conversations, data.conversation) }));
      return true;
    } catch (error) { notify(error.message); return false; }
  },

  changeRole: async (conversationId, userId, role) => {
    try {
      const data = await request(`/messenger/conversations/${conversationId}/members/${userId}`, {
        method: 'PATCH', body: JSON.stringify({ role }),
      });
      set((state) => ({ conversations: replaceConversation(state.conversations, data.conversation) }));
      return true;
    } catch (error) { notify(error.message); return false; }
  },

  removeMember: async (conversationId, userId) => {
    try {
      await request(`/messenger/conversations/${conversationId}/members/${userId}`, { method: 'DELETE' });
      if (userId === useAppStore.getState().user?.id) {
        set((state) => ({ conversations: state.conversations.filter((item) => item.id !== conversationId) }));
      } else {
        await get().loadConversation(conversationId, { silent: true });
      }
      return true;
    } catch (error) { notify(error.message); return false; }
  },

  blockUser: async (userId, blocked) => {
    try {
      const data = await request(`/messenger/blocks/${userId}`, { method: blocked ? 'PUT' : 'DELETE' });
      set((state) => ({ conversations: state.conversations.map((chat) => chat.type === 'direct' && chat.memberIds.includes(userId)
        ? { ...chat, blocked: data.blocked, blockedByMe: data.blockedByMe } : chat) }));
      return true;
    } catch (error) { notify(error.message); return false; }
  },

  report: async (payload) => {
    try {
      await request('/messenger/reports', { method: 'POST', body: JSON.stringify(payload) });
      notify('Жалоба отправлена', 'success');
      return true;
    } catch (error) { notify(error.message); return false; }
  },
}));

export const messengerUnread = (conversations) => conversations.reduce(
  (sum, chat) => sum + (chat.unread || 0), 0,
);
